import React, { ReactNode } from 'react';
import { Box, Divider } from '@mui/material';
import {
  defineMessage,
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  MessageDescriptor,
  useIntl,
} from 'react-intl';
import { get, isEqual, startCase, sumBy } from 'lodash';

import type { StatisticsUnit } from '../../../../core/src/shared/statistics-config';
import { useFormatNumber } from '../../utils/format-number';
import { Mode, Variant } from './chart-box.use-mode-select';
import { getNumberFormat } from './chart-box.utils';
import {
  GraphControlMode,
  HeatPumpStatus,
  Inverter,
  PlugActiveCondition,
} from '../../../../core/src/shared/enums';
import { ChartDataVariableId } from '../../utils/statistics';
import { useSelector } from '../../store/utils';
import { subInverterNameMapping as emsSubInverterNameMapping } from '../../utils/ems';

export const CustomTooltip = ({
  active,
  payload,
  label: at,
  mode,
  unit,
  variant,
  variableId,
  getSubStatsLabel,
}: {
  active: boolean;
  payload: {
    value: number;
    payload: {
      controlMode?: GraphControlMode;
      activeCondition?: PlugActiveCondition | HeatPumpStatus;
      wallboxAuthorizationCards?: Record<string, number> | null;
      subStats: Record<string, number>;
    };
  }[];
  label: Date;
  mode: Mode;
  unit: StatisticsUnit;
  variant: Variant;
  variableId: ChartDataVariableId;
  getSubStatsLabel?: (key: string) => string;
}) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const [rfidCards] = useSelector(({ variables }) => [
    variables.RFID_CARDS ?? {},
  ]);

  const subInverterNameMapping = useSelector(({ variables }) => {
    const inverterConfig = variables.INVERTER_CONFIG;
    const inverterStats = variables.INVERTER_STATS;

    const mapping: Record<string, { name: MessageDescriptor }> = {};
    for (const [key, config] of Object.entries(inverterConfig)) {
      if (
        config.type !== Inverter.SolaxEMS1000 &&
        config.type !== Inverter.SolaxEMS1000PRO
      ) {
        continue;
      }
      const stats = inverterStats[key];
      for (let i = 0; i < (config?.inverters?.length ?? 0); i++) {
        const detail = stats?.inverterDetail?.[i];
        mapping[`${key}||${i}`] = {
          name: get(
            emsSubInverterNameMapping[config.type],
            detail?.name ?? 0,
            defineMessage({
              id: 'ems.inverter.name.unknown',
              defaultMessage: 'Neznámý model',
            })
          ),
        };
      }
    }
    return mapping;
  }, isEqual);

  if (!(active && payload && payload.length)) {
    return null;
  }

  const time = () => <FormattedDate value={at} />;
  const datetime = () => (
    <>
      {time()} <FormattedTime value={at} />
    </>
  );
  const yearMonth = () => (
    <FormattedDate value={at} year="numeric" month="long" />
  );

  const labelMapping: Record<
    Variant,
    Record<Mode, () => ReactNode | string>
  > = {
    [Variant.Line]: {
      [Mode.Day]: datetime,
      [Mode.Week]: datetime,
      [Mode.Year]: time,
    },
    [Variant.Bar]: {
      [Mode.Day]: datetime,
      [Mode.Week]: time,
      [Mode.Year]: yearMonth,
    },
    [Variant.LineStacked]: {
      [Mode.Day]: datetime,
      [Mode.Week]: datetime,
      [Mode.Year]: time,
    },
    [Variant.BarStacked]: {
      [Mode.Day]: datetime,
      [Mode.Week]: time,
      [Mode.Year]: yearMonth,
    },
  };

  const value =
    variant === Variant.LineStacked || variant === Variant.BarStacked
      ? sumBy(payload, 'value')
      : payload[0].value;

  const controlMode = payload[0].payload.controlMode;
  const activeCondition = payload[0].payload.activeCondition;
  const wallboxAuthorizationCards =
    payload[0].payload.wallboxAuthorizationCards;
  const subStats = Object.entries(payload[0].payload.subStats);

  return (
    <Box
      sx={{
        bgcolor: (theme) =>
          theme.palette.mode === 'light' ? 'text.disabled' : '#343e42',
        color: 'common.white',
        borderRadius: 2,
        py: 1,
        px: { xs: 1.5, md: 2 },
        outline: 'none',
      }}
    >
      {labelMapping[variant][mode]()}
      <br />
      {formatNumber(...getNumberFormat(value, unit, mode, variant))}
      {controlMode && (
        <>
          <Divider sx={{ borderColor: 'white', opacity: 0.5, my: 0.5 }} />
          <strong>
            <FormattedMessage
              id="chart.tooltip.control_mode.title"
              defaultMessage="Řízeno podle:"
            />
          </strong>{' '}
          <FormattedMessage
            id="chart.tooltip.control_mode.value"
            defaultMessage={`{mode, select,
              photovoltaic {Fotovoltaika}
              grid {Distribuční síť}
              spot {Spot}
              flexibility {Flexibilita}
              error {Chyba}
              other {{mode}}
            }`}
            values={{ mode: controlMode }}
          />
        </>
      )}
      {activeCondition && (
        <>
          <Divider sx={{ borderColor: 'white', opacity: 0.5, my: 0.5 }} />
          <strong>
            <FormattedMessage
              id="chart.tooltip.active_condition.title"
              defaultMessage="Aktuální režim:"
            />
          </strong>{' '}
          {(() => {
            const conditionMapping: Record<
              PlugActiveCondition | HeatPumpStatus,
              ReactNode
            > = {
              off: (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.off"
                  defaultMessage="Vypnuto"
                />
              ),
              manual: (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.manual"
                  defaultMessage="Manualní"
                />
              ),
              time: (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.time"
                  defaultMessage="Časový"
                />
              ),
              battery: (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.battery"
                  defaultMessage="Baterie"
                />
              ),
              overflow: (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.overflow"
                  defaultMessage="Přetok"
                />
              ),
              spot: (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.spot"
                  defaultMessage="Spot"
                />
              ),
              error: (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.error"
                  defaultMessage="Chyba"
                />
              ),
              heating: (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.heating"
                  defaultMessage="Vytápění"
                />
              ),
              cooling: (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.cooling"
                  defaultMessage="Chlazení"
                />
              ),
              pool: (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.pool"
                  defaultMessage="Ohřev bazénu"
                />
              ),
              inactive: (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.inactive"
                  defaultMessage="Neaktivní"
                />
              ),
              'not-connected': (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.not-connected"
                  defaultMessage="Nelze navázat spojení"
                />
              ),
              'compressor-blocked': (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.compressor-blocked"
                  defaultMessage="Blokace kompresoru, nízký stav baterie"
                />
              ),
              'hot-water': (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.hot-water"
                  defaultMessage="Ohřev teplé vody"
                />
              ),
              'not-licensed': (
                <FormattedMessage
                  id="chart.tooltip.active_condition.value.not-licensed"
                  defaultMessage="Není licencováno"
                />
              ),
            };

            return conditionMapping[activeCondition];
          })()}
        </>
      )}
      {wallboxAuthorizationCards &&
        Object.keys(wallboxAuthorizationCards).length > 0 && (
          <>
            <Divider sx={{ borderColor: 'white', opacity: 0.5, my: 0.5 }} />
            <strong>
              <FormattedMessage
                id="chart.tooltip.wallbox_authorization_card_id.title"
                defaultMessage="Autorizační karta:"
              />
            </strong>{' '}
            {Object.entries(wallboxAuthorizationCards).map(
              ([cardId, value]) => (
                <div key={cardId}>
                  {cardId in rfidCards ? rfidCards[cardId].name : cardId}:{' '}
                  {formatNumber(...getNumberFormat(value, unit, mode, variant))}
                </div>
              )
            )}
          </>
        )}
      {Boolean(subStats.length) && (
        <Divider sx={{ borderColor: 'white', opacity: 0.5, my: 0.5 }} />
      )}
      {subStats.map(([metric, value]) => {
        let label: ReactNode = getSubStatsLabel?.(metric) ?? (
          <>{startCase(metric)}</>
        );
        const formattedValue = formatNumber(
          ...getNumberFormat(value, unit, mode, variant)
        );

        if (
          [
            'EM_CONSUMED_GRAPH',
            'EM_OVERFLOW_GRAPH',
            'HW_POWER_GRAPH',
            'HP_CONSUMED_GRAPH',
            'EM_METER_POWER_GRAPH',
          ].includes(variableId)
        ) {
          let formattedMetricNumber = metric;
          if (variableId === 'EM_METER_POWER_GRAPH') {
            formattedMetricNumber = metric.split('-').slice(-1)[0];
          }

          label = (
            <FormattedMessage
              id='"chart.tooltip.substat.phase"'
              defaultMessage="Fáze {number, select, 0 {1} 1 {2} 2 {3} other {{number}}}"
              values={{ number: formattedMetricNumber }}
            />
          );
        }

        if (['PV_POWER_GRAPH'].includes(variableId)) {
          const parsedMetricString = /^(.*)-(\d*)$/.exec(metric);
          const parsedMetricInverter = /^(.*)\|\|(\d*)$/.exec(metric);
          if (parsedMetricString) {
            const [, parsedInverterName, stringRawNumber] = parsedMetricString;
            const stringNumber = Number(stringRawNumber) + 1;
            let inverterName = parsedInverterName;
            if (inverterName in subInverterNameMapping) {
              inverterName = intl.formatMessage(
                subInverterNameMapping[inverterName].name
              );
            } else {
              inverterName = startCase(inverterName);
            }

            label = (
              <FormattedMessage
                id='"chart.tooltip.substat.pv_string"'
                defaultMessage="{inverterName}, String {stringNumber}"
                values={{
                  inverterName,
                  stringNumber,
                }}
              />
            );
          } else if (parsedMetricInverter) {
            if (metric in subInverterNameMapping) {
              label = intl.formatMessage(subInverterNameMapping[metric].name);
            }
          }
        }

        return (
          <>
            <strong>{label}:</strong> {formattedValue}
            <br />
          </>
        );
      })}
    </Box>
  );
};
